import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  walletTransactionCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { walletEndpoints } from "@/features/wallet/api/endpoints";
import type {
  ScanTransferRequestDto,
  TransactionDto,
} from "@/features/wallet/api/dto";

/**
 * Transfert wallet-à-wallet par lecture d'un QR client
 * (`POST /Transactions/scan-transfer`). Idempotence obligatoire.
 */
export const useScanTransfer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["wallet", "transactions", "scan-transfer"],
    mutationFn: (body: ScanTransferRequestDto) =>
      mutateIdempotent<TransactionDto>({
        scopeId: "wallet.transactions.scan-transfer",
        url: walletEndpoints.transactions.scanTransfer,
        body,
      }),
    onSuccess: (data) => {
      return invalidateAfterMutation(
        queryClient,
        walletTransactionCacheKeys(data.id),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useScanTransfer failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "wallet" }
      );
    },
  });
};
